// RegionSelector.tsx
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/useDebounce";

// RegionSelector.tsx

interface Region {
  id: number;
  attributes: {
    Name: string;
    Observation?: string;
  };
}

interface RegionSelectorProps {
  onSelect: (region: Region) => void;
  selectedValue?: string;
  className?: string;
  required?: boolean;
  createButton?: React.ReactNode;
}

export function RegionSelector({
  onSelect,
  selectedValue = "",
  className = "",
  required = false,
  createButton,
}: RegionSelectorProps) {
  const { toast } = useToast();
  const [regionSelected, setRegionSelected] = useState(false);
  const [searchTerm, setSearchTerm] = useState(selectedValue);
  const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  // Atualizar quando selectedValue mudar externamente
  useEffect(() => {
    if (selectedValue) {
      setSearchTerm(selectedValue);
      setRegionSelected(true);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (regionSelected) {
      return;
    }

    if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
      const fetchFilteredRegions = async () => {
        try {
          const response = await fetch(
            `${route}/api/regions?filters[Name][$containsi]=${debouncedSearchTerm}`,
          );
          const result = await response.json();
          setFilteredRegions(result.data || []);
        } catch (error) {
          console.error("Erro ao buscar regiões:", error);
          toast({
            title: "Erro",
            description: "Erro ao buscar regiões.",
            variant: "destructive",
          });
        }
      };

      fetchFilteredRegions();
    } else {
      setFilteredRegions([]);
    }
  }, [debouncedSearchTerm, route, toast, regionSelected]);

  const handleRegionSelect = (region: Region) => {
    onSelect(region);
    setSearchTerm(region.attributes.Name);
    setFilteredRegions([]);
    setRegionSelected(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setRegionSelected(false);
  };

  return (
    <div className={`mb-5 ${className}`}>
      <Label htmlFor="region" className="text-gray-600">
        Região {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex items-center">
        <Input
          type="text"
          id="region"
          name="region"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder="Digite o nome da região"
          className="w-full"
          required={required}
        />
        {createButton && <div className="ml-2">{createButton}</div>}
      </div>

      {debouncedSearchTerm &&
        debouncedSearchTerm.length >= 2 &&
        !regionSelected && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
            {filteredRegions.length > 0 ? (
              filteredRegions.map((region) => (
                <div
                  key={region.id}
                  onClick={() => handleRegionSelect(region)}
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                >
                  <div className="font-medium">{region.attributes.Name}</div>
                  {region.attributes.Observation && (
                    <div className="text-sm text-gray-500">
                      {region.attributes.Observation}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500">
                Região não encontrada
              </div>
            )}
          </div>
        )}
    </div>
  );
}

// Componente para Multi-seleção de Regiões (para filtros)
interface RegionMultiSelectorProps {
  value: string[];
  onChange: (values: string[]) => void;
  className?: string;
  maxHeight?: string;
}

export function RegionMultiSelector({
  value,
  onChange,
  className = "",
  maxHeight = "max-h-60",
}: RegionMultiSelectorProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionCounts, setRegionCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  // Buscar regiões quando o componente monta ou quando o termo de busca muda
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoading(true);
        let url = `${route}/api/regions?pagination[pageSize]=100&sort=Name:asc`;

        if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
          url += `&filters[Name][$containsi]=${debouncedSearchTerm}`;
        }

        const response = await fetch(url);
        const result = await response.json();
        const fetchedRegions = result.data || [];
        setRegions(fetchedRegions);

        // Buscar contagem de imóveis para cada região
        const counts: Record<string, number> = {};
        await Promise.all(
          fetchedRegions.map(async (region: Region) => {
            try {
              const countResponse = await fetch(
                `${route}/api/properties?filters[region][$eq]=${encodeURIComponent(region.attributes.Name)}&pagination[pageSize]=1`
              );
              const countData = await countResponse.json();
              counts[region.attributes.Name] = countData.meta?.pagination?.total || 0;
            } catch (error) {
              console.error(`Erro ao buscar contagem para região ${region.attributes.Name}:`, error);
              counts[region.attributes.Name] = 0;
            }
          })
        );
        setRegionCounts(counts);
      } catch (error) {
        console.error("Erro ao buscar regiões:", error);
        toast({
          title: "Erro",
          description: "Erro ao buscar regiões.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchRegions();
    }
  }, [debouncedSearchTerm, isOpen, route, toast]);

  const handleToggleRegion = (regionName: string) => {
    const newValue = value.includes(regionName)
      ? value.filter((v) => v !== regionName)
      : [...value, regionName];
    onChange(newValue);
  };

  const handleRemoveRegion = (regionName: string) => {
    onChange(value.filter((v) => v !== regionName));
  };

  const getDisplayText = () => {
    if (value.length === 0) return "Selecione as regiões";
    if (value.length === 1) return value[0];
    return `${value.length} regiões selecionadas`;
  };

  return (
    <div className={`relative ${className}`}>
      <label className="mb-1 block text-base font-semibold text-gray-800">
        Região
      </label>

      {/* Selected regions display */}
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {value.map((regionName) => (
            <span
              key={regionName}
              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800"
            >
              {regionName}
              <button
                type="button"
                onClick={() => handleRemoveRegion(regionName)}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span
          className={value.length === 0 ? "text-gray-500" : "text-gray-900"}
        >
          {getDisplayText()}
        </span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} text-gray-400`}
        ></i>
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg">
          {/* Search input */}
          <div className="border-b border-gray-200 p-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar região..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded border border-gray-300 py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 transform text-sm text-gray-400"></i>
            </div>
          </div>

          {/* Regions list */}
          <div className={`${maxHeight} overflow-y-auto`}>
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Carregando...
              </div>
            ) : regions.length > 0 ? (
              regions.map((region) => {
                const isSelected = value.includes(region.attributes.Name);

                return (
                  <div
                    key={region.id}
                    onClick={() => handleToggleRegion(region.attributes.Name)}
                    className="flex cursor-pointer items-center px-3 py-2 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Controlled by onClick
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {region.attributes.Name}
                        <span className="ml-2 text-xs text-gray-500">
                          ({regionCounts[region.attributes.Name] || 0} {regionCounts[region.attributes.Name] === 1 ? 'imóvel' : 'imóveis'})
                        </span>
                      </div>
                      {region.attributes.Observation && (
                        <div className="text-xs text-gray-500">
                          {region.attributes.Observation}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-500">
                {searchTerm
                  ? "Nenhuma região encontrada"
                  : "Nenhuma região disponível"}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 p-2">
            <span className="text-xs text-gray-500">
              {value.length} selecionadas
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
